import { Link } from "react-router-dom";
import { type Language, getPageTranslations } from "../lib/translations";
import { Button } from "./ui/button";
import { Calendar, Tag, ArrowLeft, BookOpen } from "lucide-react";
import { parseMarkdownToReact } from "../lib/markdownParser";
import { type BlogPost } from "./BlogPostCard";

interface BlogPostDetailProps {
  post: BlogPost;
  lang: Language;
}

export function BlogPostDetail({ post, lang }: BlogPostDetailProps) {
  const t = getPageTranslations(lang).blog;
  return (
    <article className="max-w-3xl mx-auto py-6 space-y-6 animate-fade-in text-left">
      <Link to={`/${lang}/blog`}>
        <Button variant="ghost" size="sm" className="pl-0 text-brand-secondary hover:text-brand-primary font-semibold mb-4">
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          {t.backButton}
        </Button>
      </Link>

      <div className="space-y-3 border-b border-border/80 pb-6">
        <div className="flex flex-wrap gap-4 text-xs font-mono text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{post.date}</span>
          {post.readingTime !== undefined && (
            <span className="flex items-center gap-1">
              <BookOpen className="h-3.5 w-3.5" />
              {post.readingTime} {t.minRead || "min read"}
            </span>
          )}
        </div>
        <h2 className="text-3xl md:text-4xl font-extrabold text-brand-primary leading-tight">
          {post.title}
        </h2>
        <div className="flex flex-wrap gap-1.5 pt-2">
          {post.categories && post.categories.map((cat) => (
            <span key={cat} className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-primary bg-brand-primary/5 border border-brand-primary/15 px-2 py-0.5 rounded">
              {cat}
            </span>
          ))}
          {post.tags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-secondary bg-brand-secondary/5 border border-brand-secondary/15 px-2 py-0.5 rounded">
              <Tag className="h-2.5 w-2.5" />
              {tag}
            </span>
          ))}
        </div>
      </div>

      {post.description && (
        <div className="bg-brand-primary/5 border border-brand-primary/10 rounded-2xl p-5 space-y-2 text-left">
          <h4 className="text-xs uppercase font-extrabold tracking-wider text-brand-primary">
            {t.tldr || "TL;DR"}
          </h4>
          <p className="text-sm text-foreground/80 leading-relaxed italic">
            {post.description}
          </p>
        </div>
      )}

      <hr className="border-border/50" />

      <div className="article-body prose dark:prose-invert max-w-none text-foreground/90 leading-relaxed text-base space-y-5">
        {parseMarkdownToReact(post.content)}
      </div>
    </article>
  );
}
